import { ApostleClassType, ApostleSkill } from '@apeiron/apostle'
import { MayNull } from '@apeiron/temp'
import { Box, List, ListItemButton, styled, Typography } from '@mui/material'
import CustomDropdown from '@src/components/apostles/bounty/CustomDropdown'
import ApostleSkillItem from '@src/components/apostles/share/ApostleSkillItem'
import ApeironRecycleList from '@src/components/share/apeiron/ApeironRecycleList'
import SkillDescriptionTab from '@src/components/share/skill/SkillDescriptionTab'
import { SkillProvider } from '@src/contexts/skill/SkillContext'
import useFetchApostleSkillJson from '@src/hooks/json/useFetchApostleSkillJson'
import * as R from 'ramda'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const Container = styled(Box)`
  background-color: #dfdfdf;
  min-width: 370px;
  height: 400px;
  padding: 10px;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ListContainer = styled(Box)`
  height: 100%;
  position: relative;
  overflow-y: auto;
`

const StyledList = styled(List)`
  padding: 0;
  margin: 0;
  overflow-x: hidden;
  overflow-y: auto;
`

const StyledApostleSkillItem = styled(ApostleSkillItem)`
  .Custom-Image-Container {
    .Custom-Image {
      width: 70px;
    }
  }
  .Custom-Mana {
    left: 9px;
    top: 11px;
  }
  .Custom-Info-Box {
    padding-top: 6px;
  }
  .Custom-Element-Name {
    font-weight: 300;
  }
  .Custom-Name-Box {
    .Custom-Name {
      color: #333333;
      font-weight: 400;
    }
  }
  .Custom-Description {
    color: #333333;
    font-weight: 400;
    margin-top: 6px;
  }
`

const SkillName = styled(Typography)`
  margin: 0 8px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  text-transform: capitalize;
`

const ApostleSkillSelector = ({
  selectedValue,
  onChange,
  classType,
  skillType,
  disabled,
  hiddenSkillIds = []
}: Props) => {
  const { data } = useFetchApostleSkillJson()

  const skills = useMemo(() => {
    return R.isNotNil(data) ? data : []
  }, [data])

  const { t } = useTranslation()

  const selectedSkill = useMemo(
    () => skills.find(c => c.uniqueKey === selectedValue),
    [selectedValue, skills]
  )

  const filteredSkills = useMemo(() => {
    if (classType || skillType) {
      return skills.filter(
        skill =>
          (classType == null || skill.class === classType) &&
          (skillType == null || skill.type === skillType) &&
          !hiddenSkillIds.includes(skill.uniqueKey)
      )
    }

    return skills
  }, [classType, skillType, skills, hiddenSkillIds])

  const handleSelect = useCallback(
    (skill: ApostleSkill) => {
      onChange(skill)
    },
    [onChange]
  )

  if (!skills) {
    return <></>
  }

  return (
    <SkillProvider>
      <CustomDropdown
        disabled={disabled}
        renderDropdownContent={({ closeDropdown }) => {
          return (
            <Container>
              <SkillDescriptionTab />
              <ListContainer>
                <StyledList>
                  <ApeironRecycleList
                    total={filteredSkills.length}
                    onRenderItem={(
                      measureRef: (ref: Element | null) => void,
                      index: number
                    ) => {
                      const skill = filteredSkills[index]

                      return (
                        <ListItemButton
                          ref={measureRef}
                          key={index}
                          onClick={() => {
                            handleSelect(skill)
                            closeDropdown()
                          }}
                        >
                          <StyledApostleSkillItem
                            key={skill.uniqueKey}
                            skill={skill}
                          />
                        </ListItemButton>
                      )
                    }}
                  />
                </StyledList>
              </ListContainer>
            </Container>
          )
        }}
      >
        <SkillName>
          {selectedValue
            ? selectedSkill?.name
            : t('bounty.apostle.create_dialog.unselected')}
        </SkillName>
      </CustomDropdown>
    </SkillProvider>
  )
}

type Props = {
  classType?: ApostleClassType
  skillType?: ApostleSkill['type']
  selectedValue: ApostleSkill['uniqueKey'] | null
  onChange: (value: ApostleSkill) => void
  disabled?: boolean
  hiddenSkillIds?: MayNull<string>[]
}

export default ApostleSkillSelector
